import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { MatLabel } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { ReviewService } from '../../../services/review.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { ExportTitleCaseMap } from '../../../models/PdfRelatedModels';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { MatRadioModule } from '@angular/material/radio';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { CostReviewMarginExceptions } from '../../../models/CostReviewMarginExceptions';
import { ActionNeededField } from '../../../models/ActionNeededFieldChange';
import { CostReviewBaseAttributes } from '../../../models/CostReviewBaseAttributes';
import { CostReviewSummary } from '../../../models/CostReviewSummary';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { SiComboBoxComponent } from '../../Si-Web-Toolkit/si-combo-box/si-combo-box.component';

@Component({
  selector: 'app-sol-margins-tab',
  imports: [
    MatLabel,
    CommonModule,
    TranslatePipe,
    MatRadioModule,
    SiRadioButtonComponent,
    SiFormFieldComponent,
    SiDropDownComponent,
    ReactiveFormsModule,
    DecimalPrecisionPipe,
    MultilingualCurrencyPipe,
    TrackChangesDirective,
    SiComboBoxComponent
],
  providers: [DecimalPrecisionPipe, MultilingualCurrencyPipe],
  templateUrl: './sol-margins-tab.component.html',
  styleUrl: './sol-margins-tab.component.scss'
})
export class SolMarginsTabComponent implements OnInit, OnDestroy {
  constants = Constants;
  costReviewMarginExceptions: CostReviewMarginExceptions | undefined = new CostReviewMarginExceptions();
  costReviewSummary: CostReviewSummary | undefined = new CostReviewSummary();
  reasonForExceptionOptions: Array<{ label: string; value: string }> = [];
  @Input() showSectionNotApplicableMsg: boolean = true;
  destroy$ = new Subject<void>();
  private business: string = '';
  @Output() disablePdf = new EventEmitter<boolean>();
  costReviewMarginsFormGroup! : FormGroup<{
  systemType: FormControl<string>;
  smBgmApproval: FormControl<string>;
  approverEmailId: FormControl<string>;
  reasonForException: FormControl<string>;
}>;
  isReadOnlyMode:boolean = false;
  isDisabled: boolean = false;
  variance: number = 0;

  constructor(private commonDataService: CommonDataService,
    private commonFunctionsService: CommonFunctionsService,
    private readonly reviewService: ReviewService,
    private translate: TranslateService,
    private readonly decimalPrecision: DecimalPrecisionPipe,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailTabsService: ReviewDetailTabsService) { }

  ngOnInit(): void {
    this.isReadOnlyMode = this.commonDataService.libraryReadOnlyMode;
    this.business = this.reviewService.getBusiness();
    this.populateReasonForExceptionOptions();
    this.initializeMarginData();
    this.initializeMarginFormData();
    this.setupReadOnlyMode();
    this.subscribeToDataRefresh();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_MARGINS);
      });
    this.sendDisablePdfEvent();
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadOnlyMode = false;
          this.costReviewMarginsFormGroup.enable();
        } else {
          this.isReadOnlyMode = readOnly;
           this.costReviewMarginsFormGroup.disable();
        }
        if (!readOnly) {
          this.subscribeToFormChanges();
        }
      });
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        this.isDisabled = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisabled;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
        this.initializeMarginData();
        this.mapMarginDataToForm();
      });
  }

  private populateReasonForExceptionOptions(): void {
    const selectedParameterKey = this.getMarginExceptionParameterKeyByBusiness(this.reviewService.getBusiness());

    if (!selectedParameterKey) {
      this.reasonForExceptionOptions = [];
      return;
    }

    const parameterPrefix = `${selectedParameterKey}_`;
    this.reasonForExceptionOptions = this.reviewService.costReviewParameters
      .filter((parameter) => parameter.parameterID.startsWith(parameterPrefix))
      .sort((a, b) => a.key - b.key)
      .map((parameter) => ({
        label: parameter.description,
        value: parameter.description,
      }));
  }

  private getMarginExceptionParameterKeyByBusiness(business: string): string {
    switch (business) {
      case this.constants.BUSINESS_AUTOMATION:
        return this.constants.MARGIN_EXCEPTION_AUTOMATION;
      case this.constants.BUSINESS_SECURITY:
        return this.constants.MARGIN_EXCEPTION_SECURITY;
      case this.constants.BUSINESS_FIRE:
        return this.constants.MARGIN_EXCEPTION_FIRE;
      case this.constants.BUSINESS_MECHANICAL:
        return this.constants.MARGIN_EXCEPTION_MECHANICAL;
      case this.constants.BUSINESS_ELECTRICAL:
        return this.constants.MARGIN_EXCEPTION_ELECTRICAL;
      default:
        return Constants.EMPTY_STRING;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  initializeMarginData() {
    const data: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    if (!data || !data.reviewObject) {
      this.costReviewMarginExceptions = undefined;
      this.costReviewSummary = undefined;
      return;
    }

    this.costReviewMarginExceptions = data.reviewObject.costReviewMarginExceptions;
    this.costReviewSummary = data.reviewObject.costReviewSummary;
    this.business = this.reviewService.getBusiness();
    if(this.costReviewMarginExceptions) {
      const margin = this.costReviewMarginExceptions.estimatedMargin ?? 0;
      const minimumMargin = this.costReviewMarginExceptions.estimatedMarginMinimum ?? 0;
      this.variance = margin - minimumMargin;
    }
  }

  mapMarginDataToForm() {
    if (this.costReviewMarginExceptions === undefined || this.costReviewMarginExceptions === null) {
      return; 
    }
    this.costReviewMarginsFormGroup.patchValue({
      systemType: this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewMarginExceptions.estimateClassification),
      smBgmApproval: this.getParameterValues(this.constants.YES_NO, this.costReviewMarginExceptions.smOrBgmApprovalRecieved),
      approverEmailId: this.costReviewMarginExceptions.approverEmailId,
      reasonForException: this.costReviewMarginExceptions.reasonForException
    });
  }

  /**
   * Subscribes to form value changes, writes them back to the model,
   * and notifies the host app so it can re-evaluate action-needed visibility.
   */
  private subscribeToFormChanges(): void {
    if (this.isReadOnlyMode) {
      return;
    }

    this.costReviewMarginsFormGroup.valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe((formValues) => {
        if (!this.costReviewMarginExceptions) {
          return;
        }

        const newSmBgmApproval = formValues.smBgmApproval ?? '';
        const previousSmBgmApproval = this.getParameterValues(this.constants.YES_NO, this.costReviewMarginExceptions.smOrBgmApprovalRecieved);
        if (previousSmBgmApproval !== newSmBgmApproval) {
          const numericValue = this.commonFunctionsService.isValueEqualToTranslation(newSmBgmApproval, 'YES') ? 1 : 2;
          this.syncMarginExceptionValue('smOrBgmApprovalRecieved', numericValue);
          this.reviewService.notifyFieldChange(this.getFieldForBusiness('smOrBgmApprovalReceived'), numericValue);
        }

        const newApproverEmail = formValues.approverEmailId ?? '';
        if (this.costReviewMarginExceptions.approverEmailId !== newApproverEmail) {
          this.syncMarginExceptionValue('approverEmailId', newApproverEmail);
          this.reviewService.notifyFieldChange(this.getFieldForBusiness('approverEmailId'), newApproverEmail);
        }

        const newReason = formValues.reasonForException ?? '';
        if (this.costReviewMarginExceptions.reasonForException !== newReason) {
          this.syncMarginExceptionValue('reasonForException', newReason);
          this.reviewService.notifyFieldChange(this.getFieldForBusiness('reasonForException'), newReason);
        }
      });
  }

  initializeMarginFormData() {
     if (this.costReviewMarginExceptions === undefined || this.costReviewMarginExceptions === null) {
      return; 
    }
    this.costReviewMarginsFormGroup = this.formBuilder.group({
      systemType: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewMarginExceptions.estimateClassification) || Constants.EMPTY_STRING),
      smBgmApproval: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewMarginExceptions.smOrBgmApprovalRecieved) || Constants.EMPTY_STRING),
      approverEmailId: this.formBuilder.control(this.costReviewMarginExceptions.approverEmailId || Constants.EMPTY_STRING),
      reasonForException: this.formBuilder.control(this.costReviewMarginExceptions.reasonForException || Constants.EMPTY_STRING),
    });
  }

  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value)
  }

  private getParameterCode(parameter: string, description: string, currentValue: number): number {
    const code = this.reviewService.getCostReviewParametersCode(parameter, description);
    return code !== undefined ? code : currentValue;
  }

  private getFieldForBusiness(property: string): ActionNeededField {
    const fieldMap: Record<string, Record<string, ActionNeededField>> = {
      reasonForException: {
        default: ActionNeededField.MarginReasonForException,
        [Constants.BUSINESS_FIRE]: ActionNeededField.FireMarginReasonForException,
        [Constants.BUSINESS_SECURITY]: ActionNeededField.SecurityMarginReasonForException,
      },
      approverEmailId: {
        default: ActionNeededField.MarginApproverEmailId,
        [Constants.BUSINESS_FIRE]: ActionNeededField.FireMarginApproverEmailId,
        [Constants.BUSINESS_SECURITY]: ActionNeededField.SecurityMarginApproverEmailId,
      },
      smOrBgmApprovalReceived: {
        default: ActionNeededField.MarginSmOrBgmApprovalReceived,
        [Constants.BUSINESS_FIRE]: ActionNeededField.FireMarginSmOrBgmApprovalReceived,
        [Constants.BUSINESS_SECURITY]: ActionNeededField.SecurityMarginSmOrBgmApprovalReceived,
      },
    };
    return fieldMap[property]?.[this.business] ?? fieldMap[property]?.['default'];
  }

  private getReviewObject(): CostReviewBaseAttributes | undefined {
    return this.reviewService.costReviewDetails?.reviewObject;
  }

  private syncMarginExceptionValue<K extends keyof CostReviewMarginExceptions>(key: K, value: CostReviewMarginExceptions[K]): void {
    if (this.costReviewMarginExceptions) {
      this.costReviewMarginExceptions[key] = value;
    }

    const reviewObject = this.getReviewObject();
    if (reviewObject?.costReviewMarginExceptions) {
      reviewObject.costReviewMarginExceptions[key] = value;
    }
  }

  private exportMarginsPdf(data: any, title: string, selectedReview: ReviewDetails) {
    const options: ExportObjectAsFieldValuePDFOptions = {
      data,
      title,
      fileName: 'margins-summary.pdf',
      estimateID: selectedReview.estimateID,
      estimateName: selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.MARGINS'),
    };
    this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName !== this.constants.SOL_MARGINS) return;

    if (this.shouldShowNoData()) {
      this.exportMarginsPdf(
        { [this.translate.instant('REVIEW_SOL_MARGINS_TAB.MARGINS_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
        Constants.EMPTY_STRING,
        selectedReview
      );
      return;
    }

    const data = this.buildExportData();

    if (!data || Object.keys(data).length === Constants.ZERO) {
      this.exportMarginsPdf(
        { [this.translate.instant('REVIEW_SOL_MARGINS_TAB.MARGINS_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
        Constants.EMPTY_STRING,
        selectedReview
      );
      return;
    }

    this.exportMarginsPdf(
      data,
      this.translate.instant('REVIEW_SOL_TABS_LABELS.MARGINS'),
      selectedReview
    );
  }

  shouldShowNoData(): boolean {
    return (
      !this.costReviewMarginExceptions ||
      Object.keys(this.costReviewMarginExceptions).length === Constants.ZERO
    );
  }


  buildExportData() {
    const data: ExportTitleCaseMap = {};

    if (this.costReviewMarginExceptions?.isEstimateClassificationApplicable) {
      data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.SYSTEM_TYPE')] = this.getParameterValues(
        this.constants.SIEMENS_THIRD_PARTY,
        this.costReviewMarginExceptions.estimateClassification
      );
    }

    data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.ESTIMATED_MARGIN')] =
      `${this.decimalPrecision.transform(this.costReviewMarginExceptions?.estimatedMargin ?? Constants.ZERO)}%   [${this.translate.instant('REVIEW_SOL_MARGINS_TAB.MINIMUM')} ${this.decimalPrecision.transform(this.costReviewMarginExceptions?.estimatedMarginMinimum ?? Constants.ZERO)}% ]  ${this.translate.instant('REVIEW_SOL_MARGINS_TAB.VARIANCE')} ${this.decimalPrecision.transform(this.costReviewMarginExceptions?.estimatedMarginVariance ?? Constants.ZERO)}%`;

    if (
      this.costReviewMarginExceptions?.isReasonForExceptionApplicable &&
      !this.showSectionNotApplicableMsg
    ) {
      data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.SM_OR_BGM_APPROVAL_RECEIVED')] = this.getParameterValues(
        this.constants.YES_NO,
        this.costReviewMarginExceptions.smOrBgmApprovalRecieved
      );
    }

    if (
      this.costReviewMarginExceptions?.approverEmailId &&
      !this.showSectionNotApplicableMsg
    ) {
      data[this.translate.instant("REVIEW_SOL_MARGINS_TAB.APPROVERS_EMAIL")] = this.costReviewMarginExceptions.approverEmailId;
    }

    if (
      this.costReviewMarginExceptions?.isReasonForExceptionApplicable &&
      !this.showSectionNotApplicableMsg
    ) {
      data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.REASON_FOR_EXCEPTION')] =
        this.costReviewMarginExceptions.reasonForException ?? '';
    }

    data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.START_DATE')] = this.costReviewSummary?.projectStartDate
      ? new Date(this.costReviewSummary.projectStartDate).toLocaleDateString(this.commonDataService.selectedLocale)
      : Constants.EMPTY_STRING;

    data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.END_DATE')] = this.costReviewSummary?.projectEndDate
      ? new Date(this.costReviewSummary.projectEndDate).toLocaleDateString(this.commonDataService.selectedLocale)
      : Constants.EMPTY_STRING;

    const escalationCost = this.costReviewMarginExceptions?.escalationCost as string | number | undefined;
    let escalationCostStr = Constants.EMPTY_STRING;
    if (typeof escalationCost === 'number') {
      escalationCostStr = escalationCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    } else if (typeof escalationCost === 'string' && escalationCost.trim() !== '') {
      escalationCostStr = escalationCost;
    } else {
      escalationCostStr = '$ 0.00';
    }

    data[this.translate.instant('REVIEW_SOL_MARGINS_TAB.ESCALATION_APPLIED')] = `${this.costReviewMarginExceptions?.escalationApplied ?? Constants.EMPTY_STRING} [ ${escalationCostStr} ]`;

    return data;
  }

}
